// @file Dashboard store
// It wraps the dashboard Vuex store into a Pinia store.
import { getVuexStore } from '@@/bits/pinia'
import { defineStore } from 'pinia'
import type { AccountKey, Library, User, UserGroup, UserGroupId, UserOptions, WallCamelCase as Wall } from 'types'
import type { DashView } from 'types/dash'
import { computed } from 'vue'
import type { DashState } from 'vuexstore/dash'
import type { LibraryIdToUserRoleMap } from 'vuexstore/modules/accounts'

export const useDashboardStore = defineStore('dashboard', () => {
  // State

  // Getters
  const isNativeAccount = computed((): boolean => getVuexStore<DashState>()?.getters.isNativeAccount)
  const currentTenantLogo = computed((): string => getVuexStore<DashState>()?.getters.currentTenantLogo)
  const currentTenantName = computed((): string => getVuexStore<DashState>()?.getters.currentTenantName)
  const currentUser = computed((): User => getVuexStore<DashState>()?.getters.currentUser)
  const currentUserOptions = computed((): UserOptions => getVuexStore<DashState>()?.getters.currentUserOptions)
  const wallViewableAndVisibleLibrariesArray = computed(
    (): Library[] => getVuexStore()?.getters['accounts/wallViewableAndVisibleLibrariesArray'],
  )
  const wallViewableLibrariesArray = computed(
    (): Library[] => getVuexStore()?.getters['accounts/wallViewableLibrariesArray'],
  )
  const wallCreatableLibraries = computed(
    (): Library[] => getVuexStore()?.getters['accounts/wallCreatableLibrariesArray'],
  )

  const userRolesByLibraryId = computed(
    (): LibraryIdToUserRoleMap => getVuexStore()?.getters['accounts/userRolesByLibraryId'],
  )
  const defaultLibrary = computed((): Library | undefined => getVuexStore()?.getters.defaultLibrary)

  const isActiveCollectionCrossLibrary = computed(
    (): boolean => getVuexStore()?.getters['collections/isActiveCollectionCrossLibrary'],
  )
  const currentAccountKey = computed((): AccountKey | null => getVuexStore()?.getters['collections/activeAccountKey'])

  const activeCollectionWalls = computed((): Wall[] => getVuexStore()?.getters['collections/activeCollectionWalls'])
  const activeUserGroups = computed((): UserGroup[] => getVuexStore()?.getters['collections/activeUserGroups'])
  const isActiveGroupId = computed(
    (): ((groupId: UserGroupId) => boolean) => getVuexStore()?.getters['collections/isActiveGroupId'],
  )

  // Actions
  function createWallAndRefetchData(payload: { layout: string; groupBySection?: boolean }): void {
    void getVuexStore<DashState>()?.dispatch('createWallAndRefetchData', payload, { root: true })
  }

  function closeLayoutPicker(): void {
    void getVuexStore<DashState>()?.dispatch('closeLayoutPicker', { root: true })
  }

  function switchView(dashView: DashView): void {
    void getVuexStore<DashState>()?.dispatch('switchView', dashView, { root: true })
  }

  function promptNameForActiveUserNewFolder(): void {
    void getVuexStore<DashState>()?.dispatch('promptNameForActiveUserNewFolder', { root: true })
  }

  return {
    // State

    // Getters
    isNativeAccount,
    currentTenantLogo,
    currentTenantName,
    currentUser,
    currentUserOptions,
    wallViewableAndVisibleLibrariesArray,
    wallViewableLibrariesArray,
    wallCreatableLibraries,
    userRolesByLibraryId,
    defaultLibrary,
    isActiveCollectionCrossLibrary,
    currentAccountKey,
    activeCollectionWalls,
    activeUserGroups,
    isActiveGroupId,

    // Actions
    createWallAndRefetchData,
    closeLayoutPicker,
    switchView,
    promptNameForActiveUserNewFolder,
  }
})
